import type * as api from '@gitbook/api-client';
import {
    type ContentReferenceContext,
    isValidContentReferenceInContext,
    type ResolveContentRefOptions,
    type ResolvedContentReference,
    resolveContentRef,
    resolveScreenToContentRef,
} from '@gitbook/app-database';
import type { NavigationScreenState } from '@gitbook/app-navigation';
import type { HiveClient } from '@gitbook/hive-client';
import { createCache, createFastCacheKeyWithCollisionRisk } from '@gitbook/util-suspense-cache';

import { apiQuery, apiQueryClient } from '../../state';

export type ResolutionOptions = Pick<ResolveContentRefOptions, 'resolveAnchorText'>;

/**
 * Cache to resolve content ref to visual information.
 */
export const resolveContentRefCache = createCache<
    | readonly [
          HiveClient,
          api.ContentRef | null,
          ContentReferenceContext,
          ResolutionOptions | undefined,
      ]
    | [HiveClient, api.ContentRef | null, ContentReferenceContext],
    [ResolvedContentReference<api.Space> | null, boolean]
>({
    getKey: (input) => {
        const [_hiveClient, contentRef, context, options] = input;
        return createFastCacheKeyWithCollisionRisk([contentRef, context, options ?? {}]);
    },
    getValue: async ([hiveClient, contentRef, context, options]) => {
        if (!contentRef || !isValidContentReferenceInContext(contentRef, context)) {
            return [null, false];
        }

        try {
            let hadPermissionsError = false;
            const resolved = await resolveContentRef(contentRef, context, {
                resolveAnchorText: options?.resolveAnchorText,
                hiveClient,
                getSpace: async (spaceId) => {
                    try {
                        const space = await apiQueryClient.ensureQueryData(
                            apiQuery('getSpaceById', { spaceId })
                        );
                        return space;
                    } catch (error) {
                        if (error.statusCode === 404) {
                            return null;
                        }

                        if (error.statusCode === 401 || error.statusCode === 403) {
                            hadPermissionsError = true;
                            return null;
                        }

                        throw error;
                    }
                },
                getCollection: async (collectionId) => {
                    try {
                        const collection = await apiQueryClient.ensureQueryData(
                            apiQuery('getCollectionById', { collectionId })
                        );

                        return collection;
                    } catch (error) {
                        if (error.statusCode === 404) {
                            return null;
                        }

                        if (error.statusCode === 401 || error.statusCode === 403) {
                            hadPermissionsError = true;
                            return null;
                        }

                        throw error;
                    }
                },
                getUser: async (userId) => {
                    return apiQueryClient.ensureQueryData(
                        apiQuery('getUserById', { userId }, { nullOnError: true })
                    );
                },
            });

            return [resolved, hadPermissionsError];
        } catch (error) {
            if (error.statusCode === 404) {
                return [null, false];
            }

            if (error.statusCode === 401 || error.statusCode === 403) {
                return [null, true];
            }

            throw error;
        }
    },
    toJS(value) {
        return [value[0], value[1]];
    },
    fromJS(value) {
        return [value[0], value[1]];
    },
});

/**
 * Cache to resolve a navigation screen to a content ref.
 */
export const resolveScreenToContentRefCache = createCache<
    [HiveClient, NavigationScreenState, ContentReferenceContext],
    [api.ContentRef | null, boolean]
>({
    getKey: (input) => {
        const [, ...keyInputs] = input;
        return createFastCacheKeyWithCollisionRisk(keyInputs);
    },
    getValue: async ([hiveClient, contentRef, context]) => {
        try {
            let hadPermissionsError = false;
            const resolved = await resolveScreenToContentRef(contentRef, context, {
                hiveClient,
                getSpace: async (spaceId) => {
                    try {
                        const space = await apiQueryClient.ensureQueryData(
                            apiQuery('getSpaceById', { spaceId })
                        );
                        return space;
                    } catch (error) {
                        if (error.statusCode === 404) {
                            return null;
                        }

                        if (error.statusCode === 401 || error.statusCode === 403) {
                            hadPermissionsError = true;
                            return null;
                        }

                        throw error;
                    }
                },
                getCollection: async (collectionId) => {
                    try {
                        const collection = await apiQueryClient.ensureQueryData(
                            apiQuery('getCollectionById', { collectionId })
                        );

                        return collection;
                    } catch (error) {
                        if (error.statusCode === 404) {
                            return null;
                        }

                        if (error.statusCode === 401 || error.statusCode === 403) {
                            hadPermissionsError = true;
                            return null;
                        }

                        throw error;
                    }
                },
            });

            return [resolved, hadPermissionsError];
        } catch (error) {
            if (error.statusCode === 404) {
                return [null, false];
            }

            if (error.statusCode === 401 || error.statusCode === 403) {
                return [null, true];
            }

            throw error;
        }
    },
    toJS(value) {
        return [value[0], value[1]];
    },
    fromJS(value) {
        return [value[0], value[1]];
    },
});
